import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastMutation } from '../utils/mutation';

export interface Report {
  id: string;
  projectId: string;
  type: string;
  format: string;
  name: string;
  status: string;
  path: string | null;
  bytes: number;
  directDownloadPath: string | null;
}

export interface ReportPage {
  pageNumber: number;
  totalPages: number;
  numberReports: number;
  totalReports: number;
  reports: Report[];
}

const LIMIT = 20;

export function useReports(projectId: string, page: number) {
  return useQuery<ReportPage>({
    queryKey: ['reports', projectId, page],
    queryFn: () => api.get<ReportPage>(`/project/${projectId}/report?page=${page}&limit=${LIMIT}`),
    enabled: !!projectId,
    // Keep the current page's rows on screen while the next page loads.
    placeholderData: keepPreviousData,
  });
}

export interface ReportField {
  type: string;
  name: string;
  outputName: string;
  isEnabled: boolean;
}

export interface CreateReportInput {
  projectId: string;
  type: 'pre_processing_report' | 'post_processing_report';
  format: string;
  name: string;
  multiValueCharCode: number;
  dateTimeFormat: string;
  hasHeaderRow: boolean;
  fields: ReportField[];
  collectionIds: string[];
  columnFilters: { operator: number; fields: unknown[]; searchExpressions: unknown[] };
}

export function useCreateReport(projectId: string) {
  return useToastMutation<unknown, CreateReportInput>(
    ['reports', projectId],
    (input) => api.post(`/project/${projectId}/report`, input),
    {
      success: (_d, v) => ({ title: 'Report started', description: `Name: ${v.name}`, timeout: 5000 }),
    },
  );
}
